import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

function AudioWaveform({ analyzerNode, isActive }) {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const smoothedDataRef = useRef(null);

  useEffect(() => {
    if (!canvasRef.current || !analyzerNode || !isActive) {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
      return undefined;
    }

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const bufferLength = analyzerNode.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    const barCount = 32;

    if (!smoothedDataRef.current || smoothedDataRef.current.length !== barCount) {
      smoothedDataRef.current = new Array(barCount).fill(0);
    }

    const draw = () => {
      if (!isActive) return;
      animationRef.current = requestAnimationFrame(draw);

      analyzerNode.getByteFrequencyData(dataArray);

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const barWidth = (canvas.width / barCount) * 0.6;
      const gap = (canvas.width / barCount) * 0.4;
      const step = Math.floor(bufferLength / barCount);
      const centerY = canvas.height / 2;
      const maxBarHeight = canvas.height * 0.4;
      const minBarHeight = 4;

      for (let i = 0; i < barCount; i += 1) {
        let sum = 0;
        for (let j = 0; j < step; j += 1) {
          sum += dataArray[i * step + j];
        }
        const rawValue = sum / step;

        const smoothingFactor = 0.3;
        const prevValue = smoothedDataRef.current[i];
        smoothedDataRef.current[i] = prevValue * (1 - smoothingFactor) + rawValue * smoothingFactor;

        const threshold = 15;
        const value = smoothedDataRef.current[i] > threshold ? smoothedDataRef.current[i] : 0;

        const barHeight = Math.max(minBarHeight, (value / 255) * maxBarHeight);
        const x = i * (barWidth + gap) + gap / 2;

        const gradient = ctx.createLinearGradient(x, centerY - barHeight, x, centerY + barHeight);
        gradient.addColorStop(0, '#1976d2');
        gradient.addColorStop(0.5, '#1565c0');
        gradient.addColorStop(1, '#1976d2');

        ctx.fillStyle = gradient;
        ctx.beginPath();
        if (ctx.roundRect) {
          ctx.roundRect(x, centerY - barHeight, barWidth, barHeight * 2, barWidth / 2);
        } else {
          ctx.rect(x, centerY - barHeight, barWidth, barHeight * 2);
        }
        ctx.fill();
      }
    };

    draw();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [analyzerNode, isActive]);

  return (
    <canvas
      ref={canvasRef}
      width={320}
      height={80}
      style={{ width: '100%', maxWidth: 320, height: 80 }}
    />
  );
}

AudioWaveform.propTypes = {
  analyzerNode: PropTypes.shape({
    frequencyBinCount: PropTypes.number,
    getByteFrequencyData: PropTypes.func,
  }),
  isActive: PropTypes.bool.isRequired,
};

AudioWaveform.defaultProps = {
  analyzerNode: null,
};

export default AudioWaveform;
